/**
 * "Settings." — the title, the tab row, and each tab's body, from the
 * prototype (lines ~298-397). Memory is the one tab with a second region: the
 * plain-language edit box sits below the scroll area as its own flex child,
 * the same shape Chat.tsx uses for its composer, so it never scrolls out of
 * reach of a long memory list.
 *
 * Memory's own state (the doc list, the busy edit, the changes it returns)
 * lives in `useMemoryData` so the scrollable body and the fixed box below it
 * — two different subtrees of this page's return — can both read and change
 * it.
 */

import { useRouter } from '../router';
import { useShell } from '../components/Shell';
import { AutomationsTab } from '../components/settings/AutomationsTab';
import { ConnectorsTab } from '../components/settings/ConnectorsTab';
import { ComputerTab } from '../components/settings/ComputerTab';
import { DeveloperTab } from '../components/settings/DeveloperTab';
import { MemoryComposer, MemoryDetail, MemoryImport, MemoryList, useMemoryData } from '../components/settings/MemoryTab';
import { ModelsTab } from '../components/settings/ModelsTab';
import { SkillsTab } from '../components/settings/SkillsTab';
import { DesignTab } from '../components/settings/DesignTab';
import { UsageTab } from '../components/settings/UsageTab';
import { inDesktop } from '../desktop';
import { Ph } from '../components/ui';
import { useApp } from '../state';

export type SettingsTab = 'usage' | 'models' | 'connectors' | 'computer' | 'memory' | 'skills' | 'design' | 'automations' | 'developer';

const TABS: { tab: SettingsTab; label: string; icon: string }[] = [
  { tab: 'usage', label: 'Usage', icon: 'gauge' },
  { tab: 'models', label: 'Models', icon: 'cpu' },
  { tab: 'connectors', label: 'Connectors', icon: 'plugs-connected' },
  { tab: 'computer', label: 'This computer', icon: 'desktop' },
  { tab: 'memory', label: 'Memory', icon: 'brain' },
  { tab: 'skills', label: 'Skills', icon: 'lightning' },
  { tab: 'design', label: 'Design systems', icon: 'palette' },
  { tab: 'automations', label: 'Automations', icon: 'clock-countdown' },
  // Mauvely developers alone.
  { tab: 'developer', label: 'Developer', icon: 'code' }
];

export function SettingsPage({ tab, docId }: { tab: SettingsTab; docId: string | null }) {
  const { navigate } = useRouter();
  const { narrow } = useShell();
  const { me } = useApp();
  const developer = me?.plan === 'developer';
  const desktop = inDesktop();
  const memory = useMemoryData();

  const doc = docId ? memory.docs?.find((d) => d.id === docId) ?? null : null;

  return (
    <>
      <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div
          style={{
            maxWidth: 880,
            margin: '0 auto',
            padding: narrow ? '16px 16px 40px' : '16px 28px 40px',
            display: 'flex',
            flexDirection: 'column',
            gap: 20
          }}
        >
          <h1
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              fontSize: narrow ? 28 : 36,
              lineHeight: 1.02,
              color: 'var(--text-strong)'
            }}
          >
            Settings.
          </h1>

          {/* One line on a phone, scrolled sideways, rather than two. The
              padding keeps a focus ring inside the scroll area's clip. */}
          <div
            className={narrow ? 'scroll-row' : undefined}
            style={
              narrow
                ? { display: 'flex', gap: 4, flexWrap: 'nowrap', margin: -3, padding: 3 }
                : { display: 'flex', gap: 4, flexWrap: 'wrap' }
            }
          >
            {TABS.filter((t) => (developer || t.tab !== 'developer') && (desktop || t.tab !== 'computer')).map((t) => {
              const active = t.tab === tab;
              return (
                <button
                  key={t.tab}
                  type="button"
                  className="bare hov-tint"
                  onClick={() => navigate(`/settings/${t.tab}`)}
                  style={{
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '0 12px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 13,
                    background: active ? 'var(--primary-soft)' : 'transparent',
                    color: active ? 'var(--primary-text)' : 'var(--text-muted)'
                  }}
                >
                  <Ph name={t.icon} size={15} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {tab === 'usage' ? <UsageTab /> : null}
          {tab === 'models' ? <ModelsTab /> : null}
          {tab === 'connectors' ? <ConnectorsTab /> : null}
          {tab === 'computer' && desktop ? <ComputerTab /> : null}
          {tab === 'skills' ? <SkillsTab /> : null}
          {tab === 'design' ? <DesignTab /> : null}
          {tab === 'automations' ? <AutomationsTab /> : null}
          {tab === 'developer' && developer ? <DeveloperTab /> : null}
          {tab === 'memory' ? (
            doc ? (
              <MemoryDetail doc={doc} onDelete={() => memory.remove(doc.id).then(() => navigate('/settings/memory'))} />
            ) : docId ? null : (
              <>
                <MemoryList docs={memory.docs} />
                {memory.docs ? <MemoryImport onImported={memory.refresh} /> : null}
              </>
            )
          ) : null}
        </div>
      </div>

      {tab === 'memory' ? (
        <div style={{ flex: 'none', padding: narrow ? '8px 16px 16px' : '8px 28px 20px' }}>
          <div style={{ maxWidth: 824, margin: '0 auto' }}>
            <MemoryComposer
              docId={docId}
              busy={memory.busy}
              changes={memory.changes}
              onSubmit={(instruction) => void memory.applyEdit(instruction, docId ?? undefined)}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
